import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { refundPaymentRequest } from '../../api/payment';
import { customerOtpRequest } from '../../api/otp';
import { PATHS } from '../../routes/routes';
import './Refund.css';

const Refund = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const paymentId = new URLSearchParams(location.search).get('paymentId');
    const email = new URLSearchParams(location.search).get('email');

    useEffect(() => {
        if (!paymentId || !email) {
            navigate(PATHS.CUSTOMER_PORTAL);
            return;
        }
    }, [paymentId, email, navigate]);

    const handleSendOtp = async () => {
        if (!email) return;
        
        try {
            setLoading(true);
            setError('');
            await customerOtpRequest(email);
            setOtpSent(true);
            setCountdown(60);
            
            // Start countdown
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 10000);
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleRefund = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!paymentId || !email || !otp) return;

        try {
            setLoading(true);
            setError('');
            await refundPaymentRequest(paymentId, email, otp);
            setSuccess(true);
            
            // Redirect to customer portal after 3 seconds
            setTimeout(() => {
                navigate(PATHS.CUSTOMER_PORTAL);
            }, 3000);
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to process refund';
            
            // Handle specific error cases
            if (errorMessage.includes('already been refunded')) {
                setError('This membership has already been cancelled and refunded.');
            } else if (errorMessage.includes('already been completed')) {
                setError('This membership has already been completed and cannot be cancelled. Please contact support for assistance.');
            } else if (errorMessage.includes('failed')) {
                setError('This payment has failed and cannot be cancelled.');
            } else if (errorMessage.includes('Cannot cancel payment')) {
                setError('This membership cannot be cancelled at this time. Please contact support for assistance.');
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Refund Successful!</h2>
                    <p className="text-gray-600 mb-4">
                        Your membership has been cancelled and refund will be processed within 5-7 business days.
                    </p>
                    <p className="text-sm text-gray-500">
                        Redirecting to customer portal...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Cancel Membership</h1>
                    <p className="text-gray-600">
                        Please verify your identity to cancel this membership
                    </p>
                </div>

                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Membership Details</h3>
                    <p className="text-sm text-gray-600">
                        <strong>Payment ID:</strong> {paymentId}
                    </p>
                    <p className="text-sm text-gray-600">
                        <strong>Email:</strong> {email}
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {!otpSent ? (
                    <div className="text-center">
                        <p className="text-gray-600 mb-4">
                            We'll send a verification code to your email address
                        </p>
                        <button
                            onClick={handleSendOtp}
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded transition-colors"
                        >
                            {loading ? 'Sending...' : 'Send Verification Code'}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleRefund}>
                        <div className="mb-4">
                            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                                Enter Verification Code
                            </label>
                            <input
                                type="text"
                                id="otp"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder="Enter 6-digit code"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                maxLength={6}
                                required
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={countdown > 0 || loading}
                                className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded transition-colors"
                            >
                                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                            </button>
                            <button
                                type="submit"
                                disabled={loading || otp.length !== 6}
                                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-2 px-4 rounded transition-colors"
                            >
                                {loading ? 'Processing...' : 'Cancel Membership'}
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate(PATHS.CUSTOMER_PORTAL)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                        ← Back to Customer Portal
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Refund;
